import { useMutation } from '@tanstack/react-query';
import {
  giftContributionDetails,
  giftContributorProfilePhotoUpload,
  giftMediaComplete,
  giftMediaInitiate,
  giftTextMessage,
  openBanking,
  openBankingRestart,
} from '@/api/gifting/gifting';
import { uploadBlob } from '@/utils/helpers/network.helpers';
import { resolveAudioUploadContentType } from '@/utils/helpers/storage.helpers';
import { miaConsoleLog } from '@/utils/mia-logger';

type UseMutationOptions = {
  onSuccess?: () => void;
  onError?: () => void;
};

// ─── Audio Upload ────────────────────────────────────────────────────────────

type AudioUploadPayload = {
  blob: Blob;
  duration: number;
  uuid: string;
  code: string;
};

/**
 * Orchestrates the full audio upload sequence:
 *   1. Initiate  → get messageId + S3 uploadUrl
 *   2. PUT blob  → upload directly to S3
 *   3. Complete  → notify server the upload is done
 *
 * Throws on 409 (duplicate) or missing uploadUrl so the caller can handle
 * those cases distinctly via onError / the returned error.
 */
export const useAudioUploadMutation = ({
  onSuccess,
  onError,
}: UseMutationOptions = {}) =>
  useMutation({
    mutationFn: async ({ blob, duration, uuid, code }: AudioUploadPayload) => {
      // The recorder must hand us an explicit, allowlisted type. No silent
      // `audio/wav` fallback: mislabelling the bytes is what broke iPhone
      // playback (AVError -11800) for Android WebM recordings.
      const contentType = resolveAudioUploadContentType(blob);

      const initiateRes = await giftMediaInitiate({
        uuid,
        type: 'audio',
        contentType,
        sizeBytes: blob.size,
        code,
      });

      if (initiateRes.status === 409) {
        throw Object.assign(
          new Error('A message has already been uploaded for this gift'),
          { status: 409 },
        );
      }

      const messageId = initiateRes?.data?.messageId;
      const uploadUrl = initiateRes?.data?.uploadUrl;

      if (!uploadUrl) {
        throw Object.assign(new Error('No upload URL returned from server'), {
          status: undefined,
        });
      }

      await uploadBlob({
        method: 'put',
        url: uploadUrl,
        blob,
        contentType,
        onUploadProgress: (percentage: number) => {
          console.log(`Audio upload progress: ${percentage}%`);
        },
      });

      await giftMediaComplete({
        uuid,
        messageId,
        sizeBytes: blob.size,
        durationSec: duration,
        code,
      });
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, { uuid, code }) => {
      console.error('Audio upload error:', error);
      miaConsoleLog('error', { message: 'Gift audio upload failed', giftUuid: uuid, giftCode: code, error: String(error) });
      onError?.();
    },
  });

// ─── Video Upload ────────────────────────────────────────────────────────────

type VideoUploadPayload = {
  blob: Blob;
  duration: number;
  uuid: string;
  code: string;
};

/**
 * Orchestrates the full video upload sequence:
 *   1. Initiate  → get messageId + S3 uploadUrl
 *   2. PUT blob  → upload directly to S3
 *   3. Complete  → notify server the upload is done
 */
export const useVideoUploadMutation = ({
  onSuccess,
  onError,
}: UseMutationOptions = {}) =>
  useMutation({
    mutationFn: async ({ blob, duration, uuid, code }: VideoUploadPayload) => {
      const initiateRes = await giftMediaInitiate({
        uuid,
        type: 'video',
        contentType: 'video/mp4',
        sizeBytes: blob.size,
        code,
      });

      if (initiateRes.status === 409) {
        throw Object.assign(
          new Error('A message has already been uploaded for this gift'),
          { status: 409 },
        );
      }

      const messageId = initiateRes?.data?.messageId;
      const uploadUrl = initiateRes?.data?.uploadUrl;

      if (!uploadUrl) {
        throw Object.assign(new Error('No upload URL returned from server'), {
          status: undefined,
        });
      }

      await uploadBlob({
        method: 'put',
        url: uploadUrl,
        blob,
        contentType: 'video/mp4',
        onUploadProgress: (percentage: number) => {
          console.log(`Video upload progress: ${percentage}%`);
        },
      });

      await giftMediaComplete({
        uuid,
        code,
        messageId,
        sizeBytes: blob.size,
        durationSec: duration,
      });
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, { uuid, code }) => {
      console.error('Video upload error:', error);
      miaConsoleLog('error', { message: 'Gift video upload failed', giftUuid: uuid, giftCode: code, error: String(error) });
      onError?.();
    },
  });

// ─── Message Photo ───────────────────────────────────────────────────────────

type PhotoUploadPayload = {
  blob: Blob;
  /** The written message the photo accompanies — optional (AC 7). */
  text?: string;
  uuid: string;
  code: string;
};

/**
 * A gift-message photo (GIFTWEB-22, wire contract DEV-636).
 *
 * The photo is its own three-step handshake and is **independent** of the
 * text / voice / video message:
 *   - `media/init` is told `type: "image"` and hands back an upload URL but
 *     **no messageId** — a photo is not a message.
 *   - `media/complete` must therefore be told `type: "image"` too. Omit it and
 *     the API takes the audio/video branch and answers
 *     400 "messageId is required", which is what broke every photo upload.
 *   - the written message travels by its own endpoint. `media/init` accepts a
 *     `text` field but silently discards it, so sending it there loses the
 *     note the gifter typed.
 *
 * The photo goes first so a failure leaves the contribution clean and the
 * gifter can still switch to voice or video. Because the two commits are
 * separate, a retry after a half-finished attempt meets a 409 — that means
 * the part is already stored, so it counts as done rather than as a failure.
 */
export const usePhotoUploadMutation = ({
  onSuccess,
  onError,
}: UseMutationOptions = {}) =>
  useMutation({
    mutationFn: async ({ blob, text, uuid, code }: PhotoUploadPayload) => {
      const contentType = blob.type || 'image/jpeg';

      let uploadUrl: string | undefined;
      try {
        const initiateRes = await giftMediaInitiate({
          uuid,
          code,
          type: 'image',
          contentType,
          sizeBytes: blob.size,
        });
        uploadUrl = initiateRes?.data?.uploadUrl;
      } catch (error) {
        // 409 = this contribution already has its photo, from an attempt that
        // got further than the caller thinks. Nothing left to upload.
        if ((error as { status?: number })?.status !== 409) throw error;
      }

      if (uploadUrl) {
        await uploadBlob({ method: 'put', url: uploadUrl, blob, contentType });

        await giftMediaComplete({
          uuid,
          code,
          type: 'image',
          sizeBytes: blob.size,
        });
      }

      if (!text) return;

      try {
        await giftTextMessage({ uuid, code, text });
      } catch (error) {
        // 409 = the note is already saved against this contribution.
        if ((error as { status?: number })?.status !== 409) throw error;
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, { uuid, code }) => {
      console.error('Photo upload error:', error);
      miaConsoleLog('error', { message: 'Gift message photo upload failed', giftUuid: uuid, giftCode: code, error: String(error) });
      onError?.();
    },
  });

// ─── Text Message ────────────────────────────────────────────────────────────

type GiftTextMessagePayload = {
  text: string;
  uuid: string;
  code: string;
};

export const useGiftTextMessageMutation = ({
  onSuccess,
  onError,
}: UseMutationOptions = {}) =>
  useMutation({
    mutationFn: (payload: GiftTextMessagePayload) => giftTextMessage(payload),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, { uuid, code }) => {
      console.error('Gift text message error:', error);
      miaConsoleLog('error', { message: 'Gift text message submission failed', giftUuid: uuid, giftCode: code, error: String(error) });
      onError?.();
    },
  });

// ─── Gift Details ─────────────────────────────────────────────────────────────

type GiftDetailsPayload = {
  uuid: string;
  code: string;
  legalFirstName: string;
  legalLastName: string;
  nameKnownAs: string;
  relationshipToChild: string;
  email: string;
  phone: string;
  privacyConsent: boolean;
  marketingOptIn: boolean;
};

export const useGiftDetailsMutation = ({
  onSuccess,
  onError,
}: UseMutationOptions = {}) =>
  useMutation({
    mutationFn: (payload: GiftDetailsPayload) =>
      giftContributionDetails(payload),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, { uuid, code }) => {
      console.error('Gift details error:', error);
      miaConsoleLog('error', { message: 'Gift contributor details submission failed', giftUuid: uuid, giftCode: code, error: String(error) });
      onError?.();
    },
  });

// ─── Profile Photo ────────────────────────────────────────────────────────────

type ProfilePhotoUploadPayload = {
  code: string;
  uuid: string;
  photo: File;
};

export const useProfilePhotoUploadMutation = () =>
  useMutation({
    mutationFn: (data: ProfilePhotoUploadPayload) =>
      giftContributorProfilePhotoUpload(data),
  });

// ─── Open Banking ─────────────────────────────────────────────────────────────

type OpenBankingPayload = {
  uuid: string;
  code: string;
};

export const useOpenBankingMutation = ({
  onSuccess,
  onError,
}: UseMutationOptions = {}) =>
  useMutation({
    mutationFn: (payload: OpenBankingPayload) => openBanking(payload),
    onSuccess: (resp) => {
      console.log('open banking success', resp);
      onSuccess?.();
    },
    onError: (error, { uuid, code }) => {
      console.error('Open banking error:', error);
      miaConsoleLog('error', { message: 'Open banking payment initiation failed', giftUuid: uuid, giftCode: code, error: String(error) });
      onError?.();
    },
  });

type OpenBankingRestartPayload = {
  uuid: string;
  code: string;
};

export const useOpenBankingRestartMutation = ({
  onSuccess,
  onError,
}: UseMutationOptions = {}) =>
  useMutation({
    mutationFn: (payload: OpenBankingRestartPayload) =>
      openBankingRestart(payload.code, payload.uuid, {}),
    onSuccess: (resp) => {
      console.log('open banking restart success', resp);
      onSuccess?.();
    },
    onError: (error, { uuid, code }) => {
      console.error('Open banking restart error:', error);
      miaConsoleLog('error', { message: 'Open banking payment restart failed', giftUuid: uuid, giftCode: code, error: String(error) });
      onError?.();
    },
  });
